import { Autocomplete, Box, Button, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Radio, RadioGroup, SelectChangeEvent, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/Receipt';
import React, { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CustomTextField from '../FormFields/CustomTextField';
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';
import CustomDataGrid from '../DataGrid/DataGrid';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { GridValueGetterParams, GridColDef } from "@mui/x-data-grid";
import { batchSettleExpenses } from '../../store/expenseStore/expenseThunk';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchCostCentres } from '../../store/costCentreStore/costCentreThunk';
import { fetchBuyerOrSupplierByName } from '../../store/accountThunk';
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Utils } from '../../utils/common-utils';
import moment from 'moment';

interface DialogExpenseSettlementFormProps {
  expenses: any[];
  partyName: string;
  partyType: string;
  open: boolean;
  onClose: (settled?: boolean) => void;
  preSelectedParty?: any;
}

const DialogExpenseSettlementForm: FC<DialogExpenseSettlementFormProps> = ({ expenses, open, onClose, preSelectedParty }) => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { costCentres } = useAppSelector(state => state.costCentres);

  const [settling, setSettling] = useState(false);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [selectedToBankAccount, setSelectedToBankAccount] = useState('');
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);
  const [toBankAccountOptions, setToBankAccountOptions] = useState<IOptions[]>([]);
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [isCash, setIsCash] = useState(true);
  const [selectedCostCentre, setSelectedCostCentre] = useState('');

  // Party search
  const [options, setOptions] = useState<any[]>(preSelectedParty ? [preSelectedParty] : []);
  const [paymentToOrFromAccount, setPaymentToOrFromAccount] = useState<any>(preSelectedParty || null);
  const [isLoading, setIsLoading] = useState(false);

  // Expenses for selected party
  const [filteredExpenses, setFilteredExpenses] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // TDS
  const [taxRateOptions, setTaxRateOptions] = useState<any[]>([]);
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<IOptions[]>([]);

  const schema = yup.object().shape({});
  const methods = useForm<any>({
    resolver: yupResolver(schema),
    mode: 'onChange', reValidateMode: 'onChange',
  });
  const { control } = methods;

  useEffect(() => {
    if (!open) return;
    const loadData = async () => {
      if (selectedAccount?._id) {
        const banks: any = await dispatch(fetchBanks(selectedAccount._id));
        if (banks.payload?.banks?.length > 0) {
          const bankOpts = banks.payload.banks.map((c: any) => ({
            label: c.accountName + '-' + c.bankName,
            value: c._id
          }));
          setFromBankAccountOptions(bankOpts);
          setToBankAccountOptions(bankOpts);
          setSelectedFromBankAccount(banks.payload.banks[0]._id);
        }
      }
      try {
        const data: any = await dispatch(fetchTaxRates()).unwrap();
        if (data && data.length > 0) {
          setTaxRateOptions(data);
          const tdsRates = data.filter((item: any) => item.taxType === "tds");
          setTdsTaxRateOptions(tdsRates.map((item: any) => ({
            label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
            value: item._id
          })));
        }
      } catch (err) {}
    };
    loadData();
    dispatch(fetchCostCentres({ activeOnly: true }));
  }, [open, selectedAccount?._id, dispatch]);

  useEffect(() => { setIsCash(paymentMode === 'CASH'); }, [paymentMode]);

  // Filter expenses when party changes
  useEffect(() => {
    if (paymentToOrFromAccount) {
      const partyId = paymentToOrFromAccount._id;
      const matched = expenses.filter(e =>
        e.partyId === partyId || e.paymentTo?._id === partyId || e.employee?._id === partyId
      ).map(e => ({ ...e, paymentAmount: e.amount || 0, tdsSelected: e.tdsSelected || { apply: false } }));
      setFilteredExpenses(matched);
      setSelectedRows(matched.map(e => e._id));
    } else {
      setFilteredExpenses([]);
      setSelectedRows([]);
    }
  }, [paymentToOrFromAccount, expenses]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMode(event.target.value);
  };

  // Party search
  const onInputChangeHandler = async (_event: React.SyntheticEvent, value: string) => {
    if (value.length > 2) {
      setIsLoading(true);
      const _querystring = `name=${value}&searchFor=supplier`;
      let vendorResults: any[] = [];
      try {
        const data = await dispatch(fetchBuyerOrSupplierByName(_querystring)).unwrap();
        if (data?.accounts) vendorResults = data.accounts.map((v: any) => ({ ...v, label: v.legalName || v.name, _type: 'vendor' }));
      } catch (err) {}

      let employeeResults: any[] = [];
      if (selectedAccount?._id) {
        try {
          const empData: any = await dispatch(fetchEmployees({ accountId: selectedAccount._id, type: 'employee', params: `filter=${value}` })).unwrap();
          employeeResults = (empData?.employees || []).map((e: any) => ({ _id: e._id, name: e.name || e.empId, legalName: e.name || e.empId, _type: 'employee' }));
        } catch (err) {}
      }
      setOptions([...vendorResults, ...employeeResults]);
      setIsLoading(false);
    }
  };

  const onChangeHandler = (_event: any, value: any) => {
    setPaymentToOrFromAccount(value);
  };

  // Handle manual payment amount edit per row
  const handleManualPaymentEdit = (e: any, row: any) => {
    const val = parseFloat(e.target.value) || 0;
    setFilteredExpenses(prev => prev.map(obj =>
      obj._id === row._id ? { ...obj, paymentAmount: val, isManuallyEdited: true } : obj
    ));
  };

  // TDS handler per row
  const onChangeTdsHandler = (event: SelectChangeEvent<any>, row: any) => {
    const selectedRowId = row.row._id;
    const d = taxRateOptions.filter((item: any) => item._id === event.target.value);
    setFilteredExpenses(prev => prev.map(obj => {
      if (obj._id === selectedRowId) {
        if (!d.length) {
          return { ...obj, tdsSelected: { apply: false, totalAmount: "0.00", taxableAmount: 0, displayType: 0, tdsSelected: { _id: "", label: "", rate: 0 } } };
        }
        const tdsAmount = Number((d[0].taxRate * Number(obj.paymentAmount || obj.amount)) / 100);
        return {
          ...obj,
          tdsSelected: {
            apply: true,
            totalAmount: tdsAmount.toFixed(2),
            taxableAmount: obj.paymentAmount || obj.amount,
            displayType: 0,
            tdsSelected: { _id: d[0]._id, label: d[0].taxRate + '%', rate: d[0].taxRate }
          }
        };
      }
      return obj;
    }));
  };

  const TdsSelect = React.memo((row: any) => {
    const paymentAmount = Number(row.row?.paymentAmount || row.row?.amount || 0);
    return (
      <CustomSelect
        name="tdsSelected"
        id={`tdsSelected_${row.row?._id}`}
        label="Apply TDS"
        style={{ flex: 1, marginRight: '5px' }}
        variant="outlined"
        size="small"
        options={tdsTaxRateOptions}
        onChange={(event: SelectChangeEvent<any>) => onChangeTdsHandler(event, row)}
        value={row.row?.tdsSelected?.tdsSelected?._id || ''}
        disabled={paymentAmount <= 0}
      />
    );
  });

  const columns: GridColDef[] = [
    {
      field: "referenceId",
      headerName: 'Ref ID',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography sx={{ fontWeight: 500, color: '#6366f1', fontSize: '0.85rem' }}>
          {row.referenceId || (row.txnId ? `EXP-${String(row.txnId).padStart(3, '0')}` : '-')}
        </Typography>
      )
    },
    {
      field: "paymentDate",
      headerName: 'Date',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography sx={{ fontSize: '0.85rem' }}>{moment(row.paymentDate).format('DD MMM YYYY')}</Typography>
      )
    },
    {
      field: "expenseCategory",
      headerName: 'Category',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography sx={{ fontSize: '0.85rem' }}>{row.expenseCategory?.name || '-'}</Typography>
      )
    },
    {
      field: "amount",
      headerName: 'Amount',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {Utils.formatNumber(parseFloat(row.amount).toFixed(2))}
        </Typography>
      )
    },
    {
      field: "tds",
      headerName: 'TDS Applied',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => {
        const tdsInfo = row?.tdsSelected;
        return (
          <Box sx={{ py: 0.5, px: 0.5, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {tdsInfo?.apply && tdsInfo?.tdsSelected ? (
              <Typography sx={{ fontSize: '0.7rem', color: '#059669', fontWeight: 500, lineHeight: 1.2 }}>
                TDS {tdsInfo.tdsSelected.rate}%: {Utils.formatNumber(Number(tdsInfo.totalAmount || 0).toFixed(2))}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af', fontStyle: 'italic' }}>No TDS</Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: "tdsApply",
      headerName: '',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => <TdsSelect row={row} />
    }
  ];

  const selectedExpenses = filteredExpenses.filter(e => selectedRows.includes(e._id));
  const totalAmount = selectedExpenses.reduce((sum, e) => sum + (e.paymentAmount || e.amount || 0), 0);

  const onSubmit = async () => {
    setSettling(true);
    try {
      const data = methods.getValues();
      await dispatch(batchSettleExpenses({
        expenseIds: selectedExpenses.map(r => r._id),
        paymentMode,
        paymentDate,
        fromBankAccount: selectedFromBankAccount || undefined,
        remarks: data.remark || undefined,
        instrumentNumber: data.instrumentId || undefined
      })).unwrap();
      onClose(true);
    } catch (err) {
      console.error('Settlement failed:', err);
    }
    setSettling(false);
  };

  return (
    <div style={{ width: "100%", maxWidth: "1200px" }}>
      <FormProvider {...methods}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", padding: "2px 0 2px 2px" }} component="form" noValidate>
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: '40px' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>Expense Settlement</Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ padding: '16px 24px' }}>
            <Grid container spacing={2}>

              {/* Row 1: Party Search & Amount */}
              <Grid item xs={12} mt={1.5}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <Controller
                      name="paymentTo"
                      control={control}
                      render={() => (
                        <Autocomplete
                          id="paymentTo"
                          freeSolo
                          value={paymentToOrFromAccount || null}
                          onChange={onChangeHandler}
                          onInputChange={onInputChangeHandler}
                          options={options}
                          loading={isLoading}
                          isOptionEqualToValue={(option, value) => option?._id === value?._id}
                          getOptionLabel={option => {
                            if (typeof option === 'object' && option !== null) return option?.legalName || option?.name || "";
                            return String(option || "");
                          }}
                          renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                              <span>{option.legalName || option.name}</span>
                              <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: option._type === 'employee' ? '#1565c0' : '#49a563', fontWeight: 600 }}>
                                {option._type === 'employee' ? 'Employee' : 'Vendor'}
                              </span>
                            </li>
                          )}
                          renderInput={(params) => <CustomTextField {...params} name="paymentTo" label="Select Employee / Vendor / Account *" size="small" />}
                          style={{ width: '100%' }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomTextField
                      label="Enter Amount *"
                      name="amount"
                      type="Number"
                      variant="outlined"
                      size="small"
                      value={totalAmount}
                      disabled
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Row 2: Date, From Bank, Deposit To */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item xs={4}>
                  <DesktopDatePicker
                    label="Payment Date *"
                    inputFormat="dd/MM/yyyy"
                    maxDate={moment().add(1).toDate()}
                    value={paymentDate}
                    onChange={(value) => { if (value) setPaymentDate(value); }}
                    renderInput={(params) => <TextField {...params} name="paymentDate" required variant="outlined" size="small" fullWidth />}
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    sx={{ width: '100%', '& .MuiInputLabel-root': { transform: 'translate(14px, 9px) scale(1)', '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' } }, '& .MuiSelect-select': { padding: '8.5px 14px', display: 'flex', alignItems: 'center' } }}
                    name="fromBankAccount" label="From Bank Account" id="fromBankAccount-1" variant="outlined" size="small"
                    options={fromBankAccountOptions} value={selectedFromBankAccount ?? ""}
                    onChange={(event: SelectChangeEvent<any>) => { methods.setValue('fromBankAccount', event.target.value); setSelectedFromBankAccount(event.target.value); }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    disabled={isCash}
                    sx={{ width: '100%', '& .MuiInputLabel-root': { transform: 'translate(14px, 9px) scale(1)', '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' } }, '& .MuiSelect-select': { padding: '8.5px 14px', display: 'flex', alignItems: 'center' } }}
                    name="depositTo" label="Deposit To" id="depositTo" variant="outlined" size="small"
                    options={toBankAccountOptions} value={selectedToBankAccount ?? ""}
                    onChange={(event: SelectChangeEvent<any>) => { methods.setValue('toBankAccount', event.target.value); setSelectedToBankAccount(event.target.value); }}
                  />
                </Grid>
              </LocalizationProvider>

              {/* Cost Centre */}
              {costCentres && costCentres.length > 0 && (
                <Grid item xs={12}>
                  <CustomSelect name="costCentre" id="costCentre" label="Cost Centre (Optional)" size="small" fullWidth
                    options={costCentres.map((cc: any) => ({ label: cc.alias ? `${cc.name} (${cc.alias})` : cc.name, value: cc._id }))}
                    value={selectedCostCentre} onChange={(e: any) => setSelectedCostCentre(e.target.value)}
                  />
                </Grid>
              )}

              {/* Payment Mode */}
              <Grid item xs={12}>
                <RadioGroup row defaultValue="CASH" name="instrumentType" style={{ margin: '0px 0' }} onChange={handleRadioChange}>
                  <FormControlLabel style={{ marginRight: 20 }} value="CASH" control={<Radio size="small" />} label="Cash" />
                  <FormControlLabel style={{ marginRight: 20 }} value="CHEQUE" control={<Radio size="small" />} label="Cheque" />
                  <FormControlLabel style={{ marginRight: 20 }} value="CARD" control={<Radio size="small" />} label="Card" />
                  <FormControlLabel style={{ marginRight: 20 }} value="UPI" control={<Radio size="small" />} label="UPI" />
                  <FormControlLabel style={{ marginRight: 20 }} value="NETBANKING" control={<Radio size="small" />} label="Netbanking" />
                </RadioGroup>
              </Grid>

              {/* Ref fields */}
              <Grid item xs={3}>
                <CustomTextField disabled={isCash} variant="outlined" size="small" label="Cheque/UPI/Trxn ID" name="instrumentId" />
              </Grid>
              <Grid item xs={3}>
                <CustomTextField variant="outlined" size="small" label="Ref Note 1" name="ref1" />
              </Grid>
              <Grid item xs={3}>
                <CustomTextField variant="outlined" size="small" label="Ref Note 2" name="ref2" />
              </Grid>
              <Grid item xs={3}>
                <CustomTextField label="Remark" name="remark" variant="outlined" size="small" />
              </Grid>

              {/* Expense listing section */}
              <Grid item xs={12}>
                <Typography style={{ fontSize: 16 }}>Check the boxes to settle approved expenses.</Typography>
                <Typography style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
                  Tip: Click on selected expenses to manually edit payment amounts. Apply TDS per expense from the dropdown.
                </Typography>
              </Grid>

              <div style={{
                height: Math.min(Math.max(300, filteredExpenses.length * 80 + 80), 500),
                width: '100%',
                paddingTop: '10px'
              }}>
                {!paymentToOrFromAccount ? (
                  <Box
                    sx={{
                      height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f7fee7 100%)',
                      border: '2px dashed #a7f3d0', borderRadius: 3, position: 'relative', overflow: 'hidden',
                      '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(5, 150, 105, 0.08) 0%, transparent 50%)', pointerEvents: 'none' }
                    }}
                  >
                    <Box sx={{
                      width: 80, height: 80, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3,
                      boxShadow: '0 8px 32px rgba(5, 150, 105, 0.3)',
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': { '0%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' }, '100%': { transform: 'scale(1)' } }
                    }}>
                      <SearchIcon sx={{ fontSize: 40, color: '#ffffff' }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: '#065f46', fontWeight: 600, mb: 1 }}>Select a Party</Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', maxWidth: '280px' }}>
                      Choose a Vendor or Employee from the dropdown above to view their approved expenses
                    </Typography>
                    <Box sx={{ position: 'absolute', bottom: 20, right: 20, opacity: 0.1, transform: 'rotate(15deg)' }}>
                      <ReceiptIcon sx={{ fontSize: 60, color: '#059669' }} />
                    </Box>
                  </Box>
                ) : (
                  <CustomDataGrid
                    onSelectionModelChange={(ids) => setSelectedRows(ids as any[])}
                    selectionModel={selectedRows}
                    checkboxSelection={true}
                    disableSelectionOnClick
                    columns={columns}
                    rows={filteredExpenses}
                    getRowId={(row) => row._id}
                    rowHeight={80}
                    sx={{
                      '& .MuiDataGrid-cell': { whiteSpace: 'normal !important', wordWrap: 'break-word', overflow: 'visible !important', textOverflow: 'unset !important', lineHeight: 'normal', padding: '8px', display: 'flex', alignItems: 'flex-start' },
                      '& .MuiDataGrid-cellContent': { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }
                    }}
                  />
                )}
              </div>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ height: "40px", mb: 2 }}>
            <Grid container gap='0' sx={{ mr: '32px', pt: '10px' }}>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Button variant="outlined" style={{ marginRight: '10px' }} onClick={() => onClose(false)}>Cancel</Button>
                <LoadingButton
                  type="button" onClick={onSubmit} variant="contained" loading={settling}
                  disabled={selectedExpenses.length === 0}
                  sx={{ backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
                >
                  Settle ({selectedExpenses.length} Expenses)
                </LoadingButton>
              </Grid>
            </Grid>
          </DialogActions>
        </Box>
      </FormProvider>
    </div>
  );
};

export default DialogExpenseSettlementForm;
